import React, { useEffect, useRef, useState } from "react";
import Input from "../../shared/Input";
import Checkbox from "../../shared/Checkbox";
import Button from "../../shared/Button";
import { Link, useNavigate, useLocation, Navigate } from "react-router-dom";
import useForm from "../../hooks/useForm";
import { FORM_OPTIONS } from "./form.config";
import { useAuth } from "../../hooks/useAuth";
import useQuery from "../../hooks/useQuery";
import ContactModal from "./ContactModal";
import { PageLoading } from "../../shared";
import { toast } from "react-toastify";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import { authReturnPath } from "../../utils/authReturnPath";
import { ROOT_API_URL, STAGE } from "../../config/defaultSettings";
import { isDemoLoginPickerEnabled } from "./demoLoginGate";

const LOGIN_PREFERENCES_STORAGE_KEY = "service-login-preferences";
const DEFAULT_LOGIN_PREFERENCES = {
  email: "",
  rememberMe: false,
};
const AUTOFILL_SYNC_DELAYS = [0, 200, 500];

const Login = () => {
  const queries = useQuery();
  const { form, updateForm, isValid, setFormInit } = useForm(FORM_OPTIONS);
  const { login, user, initialized, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const email = queries.get("email");
  const [modalOpen, setModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loginPreferences, setLoginPreferences] = useLocalStorage(
    LOGIN_PREFERENCES_STORAGE_KEY,
    DEFAULT_LOGIN_PREFERENCES
  );
  const emailInputRef = useRef(null);
  const passwordInputRef = useRef(null);
  const [DemoLoginPicker, setDemoLoginPicker] = useState(null);
  const showDemoLogins = isDemoLoginPickerEnabled({
    stage: STAGE,
    hostname: typeof window !== "undefined" ? window.location.hostname : "",
    apiUrl: ROOT_API_URL,
  });

  useEffect(() => {
    const rememberedEmail = loginPreferences?.rememberMe
      ? loginPreferences?.email || ""
      : "";

    setFormInit({
      rememberMe: Boolean(loginPreferences?.rememberMe),
      email: email || rememberedEmail,
    });
  }, [
    email,
    loginPreferences?.email,
    loginPreferences?.rememberMe,
    setFormInit,
  ]);

  useEffect(() => {
    // Browser password managers can fill fields without firing React change events.
    // Syncing from the DOM keeps the controlled form state aligned with autofill.
    const syncAutofilledValues = () => {
      const nextEmail = emailInputRef.current?.value?.trim();
      const nextPassword = passwordInputRef.current?.value;

      if (nextEmail && nextEmail !== form.email) {
        updateForm({ id: "email", value: nextEmail });
      }

      if (nextPassword && nextPassword !== form.password) {
        updateForm({ id: "password", value: nextPassword });
      }
    };

    const timers = AUTOFILL_SYNC_DELAYS.map((delay) =>
      window.setTimeout(syncAutofilledValues, delay)
    );

    return () => {
      timers.forEach((timer) => window.clearTimeout(timer));
    };
  }, [updateForm]);

  useEffect(() => {
    if (!showDemoLogins) return undefined;

    let cancelled = false;

    import("./DemoLoginPicker")
      .then((module) => {
        if (!cancelled) setDemoLoginPicker(() => module.default);
      })
      .catch((error) => {
        console.error("Demo login picker failed to load", error);
      });

    return () => {
      cancelled = true;
    };
  }, [showDemoLogins]);

  function handleDemoAccount({ email: nextEmail, password }) {
    if (emailInputRef.current) emailInputRef.current.value = nextEmail;
    if (passwordInputRef.current) passwordInputRef.current.value = password;

    setFormInit({
      email: nextEmail,
      password,
      rememberMe: Boolean(form.rememberMe),
    });
  }

  useEffect(() => {
    if (initialized && user) {
      const from = authReturnPath(location.state?.from);
      navigate(from, { replace: true });
    }
  }, [initialized, user, navigate, location]);

  function handleUpdateForm(e) {
    const { value, id } = e;
    updateForm({ value, id });
  }

  function handleKeyDown(e) {
    if (e.key === "Enter" && isValid && !isButtonLoading) {
      e.preventDefault();
      handleSubmit();
    }
  }

  async function handleSubmit() {
    const credentials = {
      ...form,
      email: emailInputRef.current?.value?.trim() || form.email || "",
      password: passwordInputRef.current?.value || form.password || "",
    };

    if (credentials.email !== form.email) {
      updateForm({ id: "email", value: credentials.email });
    }

    if (credentials.password !== form.password) {
      updateForm({ id: "password", value: credentials.password });
    }

    if (!credentials.email || !credentials.password) {
      toast.error("Please fill in all required fields", {
        toastId: "validation-error",
      });
      return;
    }

    try {
      setIsSubmitting(true);
      const result = await login(credentials);

      if (result.success) {
        if (credentials.rememberMe) {
          setLoginPreferences({
            email: credentials.email,
            rememberMe: true,
          });
        } else {
          setLoginPreferences(DEFAULT_LOGIN_PREFERENCES);
        }

        toast.success("Login successful! Welcome back.", {
          toastId: "login-success",
        });
        // Navigation will be handled by the useEffect above
      } else if (result.requiresMfa) {
        navigate(
          `/login/mfa?challengeId=${encodeURIComponent(
            result.challengeId
          )}&email=${encodeURIComponent(credentials.email || "")}&maskedDestination=${encodeURIComponent(
            result.maskedDestination || ""
          )}`
        );
      } else {
        const errorMessage =
          result.error ||
          "Login failed. Please check your credentials and try again.";
        toast.error(errorMessage, {
          toastId: "login-error",
        });
      }
    } catch (err) {
      console.error("Login error:", err);
      toast.error("An unexpected error occurred. Please try again.", {
        toastId: "login-unexpected-error",
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  // If not initialized, show loading
  if (!initialized) {
    return <PageLoading />;
  }

  // If user is authenticated, redirect to home or intended destination
  if (user) {
    return <Navigate to={authReturnPath(location.state?.from)} replace />;
  }

  const isButtonLoading = isLoading || isSubmitting;

  // Show login form only if user is not authenticated
  return (
    <>
      <div className="flex flex-col items-center justify-center md:justify-start my-auto pt-8 md:pt-0 px-1 md:px-5 lg:px-24">
        <p className="text-center text-4xl font-bold pb-5">Welcome</p>
        <form
          className="w-3/4"
          autoComplete="on"
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          {showDemoLogins && DemoLoginPicker ? (
            <DemoLoginPicker onSelect={handleDemoAccount} />
          ) : null}
          <div className="my-3">
            <Input
              ref={emailInputRef}
              id="email"
              name="email"
              type="email"
              autoComplete="username"
              onChange={handleUpdateForm}
              onKeyDown={handleKeyDown}
              value={form.email}
              placeholder="Email"
            />
          </div>
          <div className="my-3">
            <Input
              ref={passwordInputRef}
              id="password"
              name="password"
              type="password"
              autoComplete="current-password"
              onChange={handleUpdateForm}
              onKeyDown={handleKeyDown}
              value={form.password}
              placeholder="Password"
            />
          </div>
          <div className="flex items-center justify-between">
            <Checkbox
              label="Remember me"
              id="rememberMe"
              checked={Boolean(form.rememberMe)}
              onChange={(e) => handleUpdateForm({ id: e.id, value: e.checked })}
            />
            <Link
              to="/forgot/password"
              className="hover:underline text-primary-600 font-semibold"
            >
              Forgot Password?
            </Link>
          </div>
          <div className="my-3">
            <Button
              type="primary"
              disabled={!isValid || isButtonLoading}
              loading={isButtonLoading}
              onClick={handleSubmit}
              className="w-full"
            >
              {isButtonLoading ? "Signing in..." : "Submit"}
            </Button>
          </div>
        </form>
        <div className="text-center pt-10">
          <p>
            Don't have an account?{" "}
            <a
              onClick={() => setModalOpen(true)}
              className="underline text-primary-600 font-semibold cursor-pointer"
            >
              Click here
            </a>
          </p>
        </div>
      </div>
    </>
  );
};

export default Login;
